import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import DoneIcon from "@mui/icons-material/Done";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/system";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { LOOKUP_FIELD_OPTIONS } from "../../../../../constants/lookupFieldOptionsDC";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setLookupFieldDataForPayload } from "../../../../../features/uniqueDefSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { UniqueDefinitionsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/UniqueDefinitionsEndpointLogic";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import PreviewPanel from "../helpers/components/PreviewPanel";

const DuplicateDefinitionLookupFields = () => {
  const {
    lookupFieldDataForPayload,
    rowFilteringDataForPayload,
    isValidRowFiltering,
    initialDuplicateData,
  } = useSelector((state: any) => state.uniqueDefinition);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Get names from URL parameters
  const destinationTableName = searchParams.get("destinationTableName") || "";

  // Find IDs by matching names with the data
  const intID: any = searchParams.get("interfaceId");

  const destTableID: any = searchParams.get("destinationTableId") || "";
  const termsList = useMemo(() => {
    return LOOKUP_FIELD_OPTIONS[destTableID] ?? [];
  }, [destTableID]);
  const navigate = useNavigate();
  const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
  const [inputValues, setInputValues] = useState({}) as any;
  const [previewValues, setPreviewValues] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const isPreviewEmpty = Object.keys(previewValues).length === 0;

  const handleInputChange = (fieldKey: string, value: string) => {
    setInputValues((prev: any) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const updateReduxState = useCallback(
    (updatedInputValues: any) => {
      if (Object.keys(updatedInputValues).length === 0) {
        // If no input values, clear the Redux state
        dispatch(setLookupFieldDataForPayload(""));
        return;
      }

      // Construct and format the payload
      const jsonPayload: any = {};
      for (const key in updatedInputValues) {
        if (updatedInputValues[key]) {
          // Only include non-empty values
          jsonPayload[key] = { lookup_field: updatedInputValues[key] };
        }
      }

      if (Object.keys(jsonPayload).length === 0) {
        dispatch(setLookupFieldDataForPayload(""));
      } else {
        const formattedPayload = JSON.stringify(jsonPayload).replace(/"/g, '"');
        dispatch(
          setLookupFieldDataForPayload(`{${formattedPayload.slice(1, -1)}}`)
        );
      }
    },
    [dispatch]
  );

  const handleRefreshPreview = () => {
    setPreviewValues({ ...inputValues });
    // Update Redux state when refresh is clicked
    updateReduxState(inputValues);
  };

  // Initialize component with initialDuplicateData
  useEffect(() => {
    if (initialDuplicateData && !isInitialized && termsList.length > 0) {
      try {
        const uniqueDefinition = JSON.parse(
          initialDuplicateData.unique_definition
        );

        // Extract field names and their lookup values
        const initialInputValues: any = {};
        const initialSelectedTerms: any[] = [];

        Object.entries(uniqueDefinition).forEach(
          ([fieldName, fieldData]: [string, any]) => {
            if (fieldData.lookup_field) {
              // Find the term in termsList that matches this field
              const matchingTerm = termsList.find(
                (term: any) => term.title === fieldName
              );

              if (matchingTerm) {
                initialSelectedTerms.push(matchingTerm);
                initialInputValues[fieldName] = fieldData.lookup_field;
              }
            }
          }
        );

        if (initialSelectedTerms.length > 0) {
          // Use setTimeout to ensure state updates happen after render
          setTimeout(() => {
            setSelectedTerms([...initialSelectedTerms]);
            setInputValues({ ...initialInputValues });
            setPreviewValues({ ...initialInputValues });

            // Update Redux state with initial data
            updateReduxState(initialInputValues);

            setIsInitialized(true);
          }, 100); // Increased timeout slightly
        } else {
          setIsInitialized(true); // Mark as initialized even if no terms found
        }
      } catch (error) {
        console.error("Error parsing initial duplicate data:", error);
        setIsInitialized(true); // Mark as initialized to prevent retry
      }
    }
  }, [
    initialDuplicateData,
    termsList.length,
    isInitialized,
    destinationTableName,
    termsList,
    updateReduxState,
  ]);

  useEffect(() => {
    // whenever the destination table name changes, clear selections and reset initialization
    setSelectedTerms([]);
    setInputValues({});
    setPreviewValues({});
    setIsInitialized(false);
    dispatch(setLookupFieldDataForPayload("")); // clear Redux payload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationTableName]);

  const handleDuplicateUniqueDefinition = async () => {
    // Validation
    if (!intID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select an interface.",
          type: "error",
        })
      );
      return;
    }

    if (!destTableID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select a destination table.",
          type: "error",
        })
      );
      return;
    }

    if (!rowFilteringDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Filtering definition is required.",
          type: "error",
        })
      );
      return;
    }

    if (!lookupFieldDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unique definition is required.",
          type: "error",
        })
      );
      return;
    }

    setIsCreating(true);

    try {
      const payload = {
        filtering_definition: rowFilteringDataForPayload,
        unique_definition: lookupFieldDataForPayload,
        backend_interface_id: Number(intID),
        destination_table_id: Number(destTableID),
      };

      const response =
        await UniqueDefinitionsEndpointLogic.createUnifierUniqueDefinition(
          oauthTokenHeader,
          payload
        );

      if (response?.status === 201) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Unique definition duplicated successfully!",
            type: "success",
          })
        );
        dispatch(setLookupFieldDataForPayload(null));
        navigate("/data_collection/unifier/unique_definitions");
      } else {
        throw new Error(
          response?.reason || "Failed to duplicate unique definition"
        );
      }
    } catch (error: any) {
      console.error("Error duplicating unique definition:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.reason ||
            error?.message ||
            "Failed to duplicate unique definition",
          type: "error",
        })
      );
    } finally {
      setIsCreating(false);
    }
  };

  const previewJsonString =
    JSON.stringify(
      Object.fromEntries(
        Object.entries(previewValues).map(([key, val]) => [
          key,
          { lookup_field: val },
        ])
      ),
      null,
      2
    ) || "{ Preview Demo }";

  return (
    <>
      <Paper sx={{ mb: 1, pb: 2 }}>
        <Box sx={{ padding: " 8px 16px 0px" }}>
          <Grid
            container
            alignItems={"flex-start"}
            spacing={8}
            justifyContent={"flex-start"}
            display={"flex"}
          >
            <Grid size={6}>
              <Stack width={"100%"}>
                <Typography variant="h6" sx={{ fontSize: "20px", mb: 3 }}>
                  Lookup Fields
                </Typography>
                <Autocomplete
                  data-tracker="300"
                  multiple
                  size="small"
                  fullWidth
                  options={termsList}
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) =>
                    option.title === value.title
                  }
                  filterSelectedOptions
                  value={selectedTerms}
                  onChange={(_, newValue) => {
                    // Determine removed terms
                    const removed = selectedTerms.filter(
                      (term) =>
                        !newValue.some(
                          (newTerm) => newTerm.title === term.title
                        )
                    );

                    // Remove from inputValues and update Redux immediately
                    const updatedInputValues = { ...inputValues };
                    removed.forEach((term) => {
                      delete updatedInputValues[term.title];
                    });

                    setSelectedTerms(newValue);
                    setInputValues(updatedInputValues);

                    // Update Redux state immediately when chips are removed
                    updateReduxState(updatedInputValues);

                    // Also update preview values to reflect the change
                    const updatedPreviewValues: any = { ...previewValues };
                    removed.forEach((term) => {
                      delete updatedPreviewValues[term.title];
                    });
                    setPreviewValues(updatedPreviewValues);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.title}
                        label={option.title}
                        sx={{
                          backgroundColor: "#7B1FA2",
                          color: "#ffffff",
                          height: "27px",
                        }}
                        deleteIcon={
                          <CancelIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8) !important",
                              fontSize: "16px !important",
                            }}
                          />
                        }
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search field names or choose from the list"
                      label="Terms In Table"
                      color="secondary"
                      sx={{ mb: 2 }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
                <Grid container spacing={2}>
                  {selectedTerms?.map((term) => (
                    <Grid size={6} key={term.title}>
                      <TextField
                        key={term.title}
                        fullWidth
                        label={term.title}
                        placeholder={`Enter value for ${term.title}`}
                        color="secondary"
                        size="small"
                        sx={{ mb: 1 }}
                        value={inputValues[term.title] || ""}
                        onChange={(e) =>
                          handleInputChange(term.title, e.target.value)
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid size={6}>
              <PreviewPanel
                json={previewJsonString || "{ Preview Demo }"}
                onRefresh={handleRefreshPreview}
                title="Definition - Preview"
              />
            </Grid>
          </Grid>
          <Grid container justifyContent={"flex-end"} mt={2}>
            <Button
              data-tracker="390"
              variant="contained"
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
              onClick={handleDuplicateUniqueDefinition}
              disabled={
                isCreating ||
                !rowFilteringDataForPayload ||
                isPreviewEmpty ||
                !intID ||
                !destTableID ||
                !isValidRowFiltering
              }
            >
              Duplicate unique definition
              {isCreating && <SimpleBackdrop />}
              <DoneIcon sx={{ width: "20px", ml: "5px" }} />
            </Button>
          </Grid>
        </Box>
      </Paper>
    </>
  );
};

export default DuplicateDefinitionLookupFields;
